export const SECRET_REPLACEMENT = '<redacted:secret>'
export const PII_REPLACEMENT = '<redacted:pii>'
export const STACK_TRACE_MAX_LENGTH = 16 * 1024
export const CUSTOM_PROPERTY_STRING_MAX_LENGTH = 4 * 1024
export const TRUNCATED_MARKER = '...[truncated]'

// Structured fields are redacted by key before inspecting their value.
const SECRET_FIELD_NAMES = new Set([
  'access_token',
  'accesstoken',
  'accountkey',
  'account_key',
  'agraph_password',
  'agraph_super_password',
  'auth',
  'authtoken',
  'api_key',
  'apikey',
  'authorization',
  'client_secret',
  'clientsecret',
  'connection_string',
  'connectionstring',
  'id_token',
  'idtoken',
  'password',
  'passwd',
  'pwd',
  'refresh_token',
  'refreshtoken',
  'secret',
  'sharedaccesskey',
  'shared_access_key',
  'token',
])

const PII_FIELD_NAMES = new Set([
  'email',
  'user',
  'user_email',
  'user_id',
  'username',
  'userName',
  'user_name',
])

const STACK_FIELD_NAMES = new Set(['stack', 'stack_trace'])

// Free-form strings can still contain secrets, PII, or copied credentials.
const emailPattern = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi
const jwtPattern = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g
const bearerPattern = /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi
const privateMaterialPattern =
  /-----BEGIN [A-Z ]*(?:PRIVATE KEY|CERTIFICATE)-----[\s\S]*?-----END [A-Z ]*(?:PRIVATE KEY|CERTIFICATE)-----/g
const uriCredentialPattern =
  /\b(?:postgres(?:ql)?|redis|mongodb|mysql|mssql):\/\/[^:\s/@]+:[^@\s]+@[^\s]+/gi
const genericCredentialPattern =
  /\b[^\s:@/]+:[^\s:@]+@[A-Za-z0-9.-]+(?::\d+)?\b/g
const secretAssignmentPattern =
  /(\b(?:access[_-]?token|account[_-]?key|api[_-]?key|authorization|client[_-]?secret|connection[_-]?string|id[_-]?token|password|passwd|pwd|refresh[_-]?token|secret|shared[_-]?access[_-]?key|token)\b\s*[:=]\s*)(["'])?[^"',;\s]+(["'])?/gi

const normalizeKey = (key) =>
  String(key)
    .replaceAll(/[^a-zA-Z0-9_]/g, '')
    .toLowerCase()

const isSecretField = (key) => SECRET_FIELD_NAMES.has(normalizeKey(key))

const isPiiField = (key) =>
  PII_FIELD_NAMES.has(String(key)) || PII_FIELD_NAMES.has(normalizeKey(key))

// Keep tabs and line breaks, but remove controls that can break log consumers.
const isUnsafeControlChar = (char) => {
  const code = char.charCodeAt(0)
  return (
    code === 0x7f ||
    (code <= 0x1f && code !== 0x09 && code !== 0x0a && code !== 0x0d)
  )
}

const stripUnsafeControls = (value) =>
  [...value].filter((char) => !isUnsafeControlChar(char)).join('')

// Prefer complete stack frames over cutting in the middle of a line.
const truncateAtLineBoundary = (value, maxLength) => {
  if (!maxLength || value.length <= maxLength) return value

  const sliceLength = Math.max(0, maxLength - TRUNCATED_MARKER.length)
  const slice = value.slice(0, sliceLength)
  const lastLineBreak = slice.lastIndexOf('\n')
  const prefix = lastLineBreak > 0 ? slice.slice(0, lastLineBreak) : slice

  return `${prefix}${TRUNCATED_MARKER}`
}

export const scrubSecrets = (value) =>
  value
    .replace(privateMaterialPattern, SECRET_REPLACEMENT)
    .replace(uriCredentialPattern, SECRET_REPLACEMENT)
    .replace(genericCredentialPattern, SECRET_REPLACEMENT)
    .replace(jwtPattern, SECRET_REPLACEMENT)
    .replace(bearerPattern, `Bearer ${SECRET_REPLACEMENT}`)
    .replace(
      secretAssignmentPattern,
      (_match, prefix, quote = '') =>
        `${prefix}${quote}${SECRET_REPLACEMENT}${quote}`,
    )

export const scrubPii = (value) => value.replace(emailPattern, PII_REPLACEMENT)

export const sanitizeLogString = (value, { maxLength } = {}) => {
  if (typeof value !== 'string') return value

  const sanitized = scrubPii(scrubSecrets(stripUnsafeControls(value)))
  return truncateAtLineBoundary(sanitized, maxLength)
}

// Recursively sanitizes envelope blocks while tolerating repeated references.
export const sanitizeLogValue = (
  value,
  { maxStringLength } = {},
  seen = new WeakSet(),
  key,
) => {
  if (value === undefined || value === null) return value

  if (key && isSecretField(key)) return SECRET_REPLACEMENT
  if (key && isPiiField(key)) return PII_REPLACEMENT

  if (typeof value === 'string') {
    const maxLength = STACK_FIELD_NAMES.has(key)
      ? STACK_TRACE_MAX_LENGTH
      : maxStringLength

    return sanitizeLogString(value, { maxLength })
  }

  if (Array.isArray(value)) {
    return value.map((item) =>
      sanitizeLogValue(item, { maxStringLength }, seen),
    )
  }

  if (typeof value !== 'object') return value

  if (seen.has(value)) return '[Circular]'
  seen.add(value)

  const sanitized = Object.entries(value).reduce((acc, [itemKey, item]) => {
    acc[itemKey] = sanitizeLogValue(item, { maxStringLength }, seen, itemKey)
    return acc
  }, {})

  seen.delete(value)
  return sanitized
}

export const sanitizeCustomProperties = (value) =>
  sanitizeLogValue(value, {
    maxStringLength: CUSTOM_PROPERTY_STRING_MAX_LENGTH,
  })

export const sanitizeErrorDetails = (error) => {
  if (!error) return undefined

  const errorObject =
    typeof error === 'object'
      ? error
      : {
          message: String(error),
        }

  return sanitizeLogValue({
    type: errorObject.type ?? errorObject.name,
    message: errorObject.message,
    stack_trace: errorObject.stack_trace ?? errorObject.stack,
    line_number: errorObject.line_number ?? errorObject.lineNumber,
    column_number: errorObject.column_number ?? errorObject.columnNumber,
  })
}

// The backend transport serializes this sanitized Error instead of the raw one.
export const createSanitizedException = (error, fallbackMessage) => {
  const details = sanitizeErrorDetails(error) || {}
  const exception = new Error(
    details.message ||
      sanitizeLogString(fallbackMessage) ||
      'Frontend exception',
  )

  exception.name = details.type || 'Error'

  if (details.stack_trace) {
    exception.stack = details.stack_trace
  }

  return exception
}
